import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
    Box,
    Button,
    TextField,
    Typography,
    Paper,
    Container,
    Grid,
    IconButton,
    InputAdornment,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { authService } from '../services/AuthService';
import ResendVerification from '../components/auth/ResendVerification';

import { authFormCardSx, authMarketingShellSx } from '../utils/authMarketingLayout';

const RegisterPage: React.FC = () => {
    const [formData, setFormData] = useState({
        email: '',
        firstname: '',
        lastname: '',
        password: '',
        confirmPassword: '',
    });
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const navigate = useNavigate();

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (name === 'password') {
            const error = validatePassword(value);
            setPasswordError(error || '');
        }
    };

    function validatePassword(password: string): string | null {
        if (password.length < 8) return 'Password must be at least 8 characters long';
        if (!/[A-Z]/.test(password)) return 'Password must contain at least one uppercase letter';
        if (!/[a-z]/.test(password)) return 'Password must contain at least one lowercase letter';
        if (!/[0-9]/.test(password)) return 'Password must contain at least one digit';
        if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) return 'Password must contain at least one special character';
        return null;
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        const passwordError = validatePassword(formData.password);
        if (passwordError) {
            setError(passwordError);
            return;
        }

        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        try {
            await authService.register({
                username: formData.email,
                email: formData.email,
                firstname: formData.firstname,
                lastname: formData.lastname,
                password: formData.password
            });
            setSuccess('Registration successful! Please check your email to verify your account.');
            setError('');
        } catch (err: any) {
            setError(err.response?.data?.message || 'Registration failed');
            setSuccess('');
        }
    };

    return (
        <Box sx={(t) => authMarketingShellSx(t)}>
            <Container component="main" maxWidth="sm" sx={{ position: 'relative', zIndex: 2 }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    {/* Logo and Title Section */}
                    <Box sx={{ mb: 4, textAlign: 'center' }}>
                        <img 
                            src="/images/logo.png" 
                            alt="Narralyze Logo" 
                            style={{ 
                                height: '60px', 
                                marginBottom: '16px',
                                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
                            }} 
                        />
                        <Typography 
                            component="h1" 
                            variant="h3" 
                            sx={{ 
                                color: 'white',
                                fontWeight: 700,
                                textShadow: '0 2px 4px rgba(0,0,0,0.5)',
                                mb: 1
                            }}
                        >
                            Join Narralyze
                        </Typography>
                        <Typography 
                            variant="h6" 
                            sx={{ 
                                color: (t) => t.palette.common.white,
                                opacity: 0.92,
                                textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                                fontWeight: 300
                            }}
                        >
                            Start your qualitative research journey
                        </Typography>
                    </Box>

                    {/* Registration Form */}
                    <Paper sx={(t) => authFormCardSx(t, { maxWidth: 500 })}>
                        {error && (
                            <Box sx={{ mb: 3, p: 2, bgcolor: 'error.light', borderRadius: '6px' }}>
                                <Typography color="error.dark" align="center">
                                    {error}
                                </Typography>
                            </Box>
                        )}
                        {success && (
                            <Box sx={{ mb: 3, p: 2, bgcolor: 'success.light', borderRadius: '6px' }}>
                                <Typography color="success.dark" align="center" gutterBottom>
                                    {success}
                                </Typography>
                                <ResendVerification />
                            </Box>
                        )}
                        
                        <Box component="form" onSubmit={handleSubmit}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        margin="normal"
                                        required
                                        fullWidth
                                        id="firstname"
                                        label="First Name"
                                        name="firstname"
                                        autoComplete="given-name"
                                        value={formData.firstname}
                                        onChange={handleChange}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '6px',
                                                '&:hover fieldset': {
                                                    borderColor: 'primary.main',
                                                },
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        margin="normal"
                                        required
                                        fullWidth
                                        id="lastname"
                                        label="Last Name"
                                        name="lastname"
                                        autoComplete="family-name"
                                        value={formData.lastname}
                                        onChange={handleChange}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '6px',
                                                '&:hover fieldset': {
                                                    borderColor: 'primary.main',
                                                },
                                            },
                                        }}
                                    />
                                </Grid>
                            </Grid>
                            
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                id="email"
                                label="Email Address"
                                name="email"
                                autoComplete="email"
                                value={formData.email}
                                onChange={handleChange}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '6px',
                                        '&:hover fieldset': {
                                            borderColor: 'primary.main',
                                        },
                                    },
                                }}
                            />
                            
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                name="password"
                                label="Password"
                                type={showPassword ? 'text' : 'password'}
                                id="password"
                                autoComplete="new-password"
                                value={formData.password}
                                onChange={handleChange}
                                error={!!passwordError}
                                helperText={passwordError || ''}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '6px',
                                        '&:hover fieldset': {
                                            borderColor: 'primary.main',
                                        },
                                    },
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={() => setShowPassword(!showPassword)}
                                                edge="end"
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                name="confirmPassword"
                                label="Confirm Password"
                                type={showPassword ? 'text' : 'password'}
                                id="confirmPassword"
                                value={formData.confirmPassword}
                                onChange={handleChange}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '6px',
                                        '&:hover fieldset': {
                                            borderColor: 'primary.main',
                                        },
                                    },
                                }}
                            />
                            
                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                sx={{ 
                                    mt: 4, 
                                    mb: 3,
                                    py: 1.5,
                                    borderRadius: '6px',
                                    fontSize: '1.1rem',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                    '&:hover': {
                                        boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
                                    }
                                }}
                            >
                                Create Account
                            </Button>
                            
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="body2">
                                    Already have an account?{' '}
                                    <Link 
                                        to="/login" 
                                        style={{ 
                                            textDecoration: 'none',
                                            color: 'primary.main',
                                            fontWeight: 500,
                                        }}
                                    >
                                        Sign in
                                    </Link>
                                </Typography>
                            </Box>
                        </Box>
                    </Paper>
                </Box>
            </Container>
        </Box>
    );
};

export default RegisterPage; 